import {
    Box,
    Center,
    Flex,
    HStack,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    SimpleGrid,
    Spinner,
    Text,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { TbCheck, TbChevronRight, TbSearch } from 'react-icons/tb';
import {
    useGetPCDBCatalog,
    type CatalogCategory,
    type CatalogSubcategory,
} from '../../../../hooks/useGetPCDBCatalog';
import { useSearchPCDB } from '../../../../hooks/useSearchPCDB';
import {
    PARTBAY_BLUE,
    PARTBAY_BLUE_TINT,
    SETTINGS_BORDER_COLOR,
} from '../../../../components/settings/primitives';
import {
    draftFromCatalogPart,
    draftFromSearchResult,
    type PartAllowanceDraft,
} from './partAllowanceDrafts';

/** Which rung of the catalog the grid is showing. */
type Level = 'category' | 'subcategory' | 'part';

const LEVEL_NOUNS: Record<Level, [string, string]> = {
    category: ['category', 'categories'],
    subcategory: ['subcategory', 'subcategories'],
    part: ['part', 'parts'],
};

export interface PartAllowanceCatalogStepProps {
    /** Part terminology ids already carried into the next step. */
    selectedIds: Set<number>;
    /** Adds the part, or drops it again when it is already selected. */
    onToggle: (draft: PartAllowanceDraft) => void;
}

/** One card in the level grid: a category, a subcategory, or a part. */
const BrowseCard = ({
    label,
    detail,
    isSelected = false,
    hasChevron = false,
    onClick,
}: {
    label: string;
    detail?: string;
    isSelected?: boolean;
    hasChevron?: boolean;
    onClick: () => void;
}) => (
    <Flex
        as="button"
        type="button"
        onClick={onClick}
        align="center"
        justify="space-between"
        gap={3}
        textAlign="left"
        px={4}
        py={3}
        borderWidth="1px"
        borderRadius="lg"
        borderColor={isSelected ? PARTBAY_BLUE : SETTINGS_BORDER_COLOR}
        bg={isSelected ? PARTBAY_BLUE_TINT : 'white'}
        transition="border-color 0.15s ease, background 0.15s ease"
        _hover={{ borderColor: PARTBAY_BLUE, bg: isSelected ? PARTBAY_BLUE_TINT : 'gray.50' }}
    >
        <Box minW={0}>
            <Text
                fontSize="sm"
                fontWeight={isSelected ? 'semibold' : 'medium'}
                color={isSelected ? PARTBAY_BLUE : 'gray.800'}
                noOfLines={2}
            >
                {label}
            </Text>
            {detail && (
                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {detail}
                </Text>
            )}
        </Box>
        {isSelected ? (
            <Flex
                boxSize="18px"
                borderRadius="full"
                bg={PARTBAY_BLUE}
                align="center"
                justify="center"
                flexShrink={0}
            >
                <Icon as={TbCheck as React.ElementType} boxSize={3} color="white" />
            </Flex>
        ) : hasChevron ? (
            <Icon
                as={TbChevronRight as React.ElementType}
                boxSize={4}
                color="gray.400"
                flexShrink={0}
            />
        ) : (
            <Box
                boxSize="18px"
                borderRadius="full"
                borderWidth="2px"
                borderColor="gray.300"
                flexShrink={0}
            />
        )}
    </Flex>
);

/**
 * Step one of the add-parts flow: pick the parts, either by searching the whole
 * catalog from the bar at the top or by walking down into it — categories, then
 * subcategories, then the parts themselves. The trail across the top says which
 * level is showing and goes back up, so the grid below is always one level and
 * the drill-down never stacks panes.
 *
 * Selecting a part ticks it and hands it to the next step; the count beside
 * that step is the running total, so this screen only has to show what is on.
 */
const PartAllowanceCatalogStep = ({
    selectedIds,
    onToggle,
}: PartAllowanceCatalogStepProps) => {
    const [category, setCategory] = useState<CatalogCategory | null>(null);
    const [subcategory, setSubcategory] = useState<CatalogSubcategory | null>(null);
    const [filter, setFilter] = useState('');
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [isSearchFocused, setIsSearchFocused] = useState(false);

    const { data: catalogData, isLoading: isCatalogLoading } = useGetPCDBCatalog();
    const { data: searchData, isLoading: isSearchLoading } = useSearchPCDB(debouncedQuery);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const level: Level = subcategory ? 'part' : category ? 'subcategory' : 'category';
    const term = filter.trim().toLowerCase();

    const categories = useMemo(
        () =>
            (catalogData?.categories ?? []).filter(
                (entry) => term === '' || entry.categoryName.toLowerCase().includes(term),
            ),
        [catalogData, term],
    );
    const subcategories = useMemo(
        () =>
            (category?.subcategories ?? []).filter(
                (entry) => term === '' || entry.subcategoryName.toLowerCase().includes(term),
            ),
        [category, term],
    );
    const parts = useMemo(
        () =>
            (subcategory?.parts ?? []).filter(
                (entry) => term === '' || entry.partTerminologyName.toLowerCase().includes(term),
            ),
        [subcategory, term],
    );

    const visibleCount =
        level === 'part' ? parts.length : level === 'subcategory' ? subcategories.length : categories.length;
    const [levelSingular, levelPlural] = LEVEL_NOUNS[level];

    const goToRoot = () => {
        setCategory(null);
        setSubcategory(null);
        setFilter('');
    };

    const goToCategory = () => {
        setSubcategory(null);
        setFilter('');
    };

    const crumbs = [
        { label: 'All Categories', onClick: level === 'category' ? undefined : goToRoot },
        ...(category
            ? [{ label: category.categoryName, onClick: level === 'part' ? goToCategory : undefined }]
            : []),
        ...(subcategory ? [{ label: subcategory.subcategoryName, onClick: undefined }] : []),
    ];

    const searchResults = searchData?.results ?? [];
    const isSearching = debouncedQuery.trim().length >= 2;

    return (
        <Flex direction="column" flex={1} minH={0} w="100%" overflow="hidden">
            <Box px={6} pt={5} pb={4} flexShrink={0}>
                {/* Global search: the whole catalog at once, for a part whose
                    category the user does not want to think about. */}
                <Box
                    position="relative"
                    zIndex={5}
                    maxW="560px"
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                            setIsSearchFocused(false);
                        }
                    }}
                >
                    <InputGroup size="md">
                        <InputLeftElement pointerEvents="none">
                            <Icon as={TbSearch as React.ElementType} color="gray.400" boxSize={4} />
                        </InputLeftElement>
                        <Input
                            value={query}
                            placeholder="Search every part by name…"
                            onChange={(e) => setQuery(e.target.value)}
                            bg="white"
                            borderRadius="md"
                        />
                    </InputGroup>

                    {isSearchFocused && isSearching && (
                        <Box
                            position="absolute"
                            top="100%"
                            left={0}
                            right={0}
                            mt={1}
                            maxH="280px"
                            overflowY="auto"
                            bg="white"
                            borderWidth="1px"
                            borderColor={SETTINGS_BORDER_COLOR}
                            borderRadius="md"
                            boxShadow="0 8px 24px -4px rgba(16, 24, 40, 0.12)"
                            p={1.5}
                            zIndex={10}
                            // Clicking a result would otherwise blur the input
                            // and close the list before the click lands.
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            {isSearchLoading ? (
                                <Center py={3}>
                                    <Spinner size="sm" color={PARTBAY_BLUE} />
                                </Center>
                            ) : searchResults.length === 0 ? (
                                <Text fontSize="sm" color="gray.500" px={3} py={2}>
                                    No parts match “{debouncedQuery}”
                                </Text>
                            ) : (
                                searchResults.slice(0, 12).map((result) => {
                                    const isSelected = selectedIds.has(result.partTerminologyId);
                                    return (
                                        <Flex
                                            key={result.partTerminologyId}
                                            as="button"
                                            type="button"
                                            w="100%"
                                            align="center"
                                            justify="space-between"
                                            gap={3}
                                            textAlign="left"
                                            px={3}
                                            py={2}
                                            borderRadius="md"
                                            bg={isSelected ? PARTBAY_BLUE_TINT : 'transparent'}
                                            _hover={{ bg: PARTBAY_BLUE_TINT }}
                                            onClick={() => onToggle(draftFromSearchResult(result))}
                                        >
                                            <Box minW={0}>
                                                <Text
                                                    fontSize="sm"
                                                    fontWeight={isSelected ? 'semibold' : 'medium'}
                                                    color={isSelected ? PARTBAY_BLUE : 'gray.800'}
                                                    noOfLines={1}
                                                >
                                                    {result.partTerminologyName}
                                                </Text>
                                                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                                    {result.categoryName} → {result.subcategoryName}
                                                </Text>
                                            </Box>
                                            {isSelected && (
                                                <Icon
                                                    as={TbCheck as React.ElementType}
                                                    boxSize={4}
                                                    color={PARTBAY_BLUE}
                                                    flexShrink={0}
                                                />
                                            )}
                                        </Flex>
                                    );
                                })
                            )}
                        </Box>
                    )}
                </Box>

                {/* The trail through the catalog, and a filter for whatever
                    level it is showing. */}
                <Flex align="center" justify="space-between" gap={4} mt={4}>
                    <HStack spacing={1.5} minW={0}>
                        {crumbs.map((crumb, index) => (
                            <HStack key={`${crumb.label}-${index}`} spacing={1.5} minW={0}>
                                {index > 0 && (
                                    <Icon
                                        as={TbChevronRight as React.ElementType}
                                        boxSize={3.5}
                                        color="gray.400"
                                        flexShrink={0}
                                    />
                                )}
                                {crumb.onClick ? (
                                    <Text
                                        as="button"
                                        type="button"
                                        fontSize="sm"
                                        fontWeight="medium"
                                        color="gray.500"
                                        noOfLines={1}
                                        _hover={{ color: PARTBAY_BLUE }}
                                        onClick={crumb.onClick}
                                    >
                                        {crumb.label}
                                    </Text>
                                ) : (
                                    <Text
                                        fontSize="sm"
                                        fontWeight="semibold"
                                        color="gray.800"
                                        noOfLines={1}
                                    >
                                        {crumb.label}
                                    </Text>
                                )}
                            </HStack>
                        ))}
                        <Text fontSize="xs" color="gray.400" flexShrink={0} pl={1}>
                            {visibleCount} {visibleCount === 1 ? levelSingular : levelPlural}
                        </Text>
                    </HStack>
                    <InputGroup size="sm" maxW="240px" flexShrink={0}>
                        <InputLeftElement pointerEvents="none">
                            <Icon as={TbSearch as React.ElementType} color="gray.400" boxSize={3.5} />
                        </InputLeftElement>
                        <Input
                            value={filter}
                            placeholder={`Filter ${levelPlural}`}
                            onChange={(e) => setFilter(e.target.value)}
                            bg="white"
                            borderRadius="md"
                        />
                    </InputGroup>
                </Flex>
            </Box>

            <Box flex={1} minH={0} overflowY="auto" px={6} pb={8}>
                {isCatalogLoading ? (
                    <Center py={10}>
                        <Spinner size="lg" color={PARTBAY_BLUE} />
                    </Center>
                ) : visibleCount === 0 ? (
                    <Text fontSize="sm" color="gray.500" py={4}>
                        {term === ''
                            ? `No ${levelPlural} here.`
                            : `No ${levelPlural} match “${filter}”.`}
                    </Text>
                ) : (
                    <SimpleGrid columns={{ base: 2, md: 3, xl: 4 }} spacing={3}>
                        {level === 'category' &&
                            categories.map((entry) => (
                                <BrowseCard
                                    key={entry.categoryId}
                                    label={entry.categoryName}
                                    detail={`${entry.subcategoryCount} subcategor${entry.subcategoryCount === 1 ? 'y' : 'ies'}`}
                                    hasChevron
                                    onClick={() => {
                                        setCategory(entry);
                                        setSubcategory(null);
                                        setFilter('');
                                    }}
                                />
                            ))}
                        {level === 'subcategory' &&
                            subcategories.map((entry) => (
                                <BrowseCard
                                    key={entry.subcategoryId}
                                    label={entry.subcategoryName}
                                    detail={`${entry.partCount} part${entry.partCount === 1 ? '' : 's'}`}
                                    hasChevron
                                    onClick={() => {
                                        setSubcategory(entry);
                                        setFilter('');
                                    }}
                                />
                            ))}
                        {level === 'part' &&
                            category &&
                            subcategory &&
                            parts.map((entry) => (
                                <BrowseCard
                                    key={entry.partTerminologyId}
                                    label={entry.partTerminologyName}
                                    isSelected={selectedIds.has(entry.partTerminologyId)}
                                    onClick={() =>
                                        onToggle(draftFromCatalogPart(entry, category, subcategory))
                                    }
                                />
                            ))}
                    </SimpleGrid>
                )}
            </Box>
        </Flex>
    );
};

export default PartAllowanceCatalogStep;
